export interface ThreadCommentDeliveryInput {
  subscriberIds: unknown;
  mutedSubscriberIds: unknown;
  historicalCommenterIds: unknown;
  participantId: string;
  actorId: string;
  mentionedUserIds: string[];
}

export interface ThreadCommentDelivery {
  subscriberIds: string[];
  mutedSubscriberIds: string[];
  recipientIds: string[];
  mentionedRecipientIds: string[];
}

const uniqueUserIds = (ids: unknown[]) => [
  ...new Set(
    ids.filter((id): id is string => typeof id === 'string' && id.length > 0),
  ),
];

export const normalizeThreadUserIds = (value: unknown): string[] => {
  if (Array.isArray(value)) return uniqueUserIds(value);
  if (!value || typeof value !== 'object') return [];

  return uniqueUserIds(
    Object.entries(value as Record<string, unknown>).flatMap(([key, item]) => {
      if (typeof item === 'string') return [item];
      if (item === true) return [key];
      return [];
    }),
  );
};

export const getEffectiveThreadSubscriberIds = ({
  subscriberIds,
  mutedSubscriberIds,
  historicalCommenterIds,
  participantId,
}: Pick<
  ThreadCommentDeliveryInput,
  | 'subscriberIds'
  | 'mutedSubscriberIds'
  | 'historicalCommenterIds'
  | 'participantId'
>) => {
  const muted = new Set(normalizeThreadUserIds(mutedSubscriberIds));
  return uniqueUserIds([
    ...normalizeThreadUserIds(subscriberIds),
    ...normalizeThreadUserIds(historicalCommenterIds),
    participantId,
  ]).filter((id) => !muted.has(id));
};

export const buildThreadCommentDelivery = ({
  subscriberIds,
  mutedSubscriberIds,
  historicalCommenterIds,
  participantId,
  actorId,
  mentionedUserIds,
}: ThreadCommentDeliveryInput): ThreadCommentDelivery => {
  const mentioned = uniqueUserIds(mentionedUserIds);
  const resubscribed = new Set([actorId, ...mentioned]);
  const nextMuted = normalizeThreadUserIds(mutedSubscriberIds).filter(
    (id) => !resubscribed.has(id),
  );
  const muted = new Set(nextMuted);
  const nextSubscribers = uniqueUserIds([
    ...normalizeThreadUserIds(subscriberIds),
    ...normalizeThreadUserIds(historicalCommenterIds),
    participantId,
    actorId,
    ...mentioned,
  ]).filter((id) => !muted.has(id));
  const recipients = nextSubscribers.filter((id) => id !== actorId);

  return {
    subscriberIds: nextSubscribers,
    mutedSubscriberIds: nextMuted,
    recipientIds: recipients,
    mentionedRecipientIds: mentioned.filter(
      (id) => id !== actorId && recipients.includes(id),
    ),
  };
};

export const toggleThreadSubscription = ({
  subscriberIds,
  mutedSubscriberIds,
  historicalCommenterIds,
  participantId,
  userId,
}: Pick<
  ThreadCommentDeliveryInput,
  | 'subscriberIds'
  | 'mutedSubscriberIds'
  | 'historicalCommenterIds'
  | 'participantId'
> & {userId: string}) => {
  const effectiveSubscribers = getEffectiveThreadSubscriberIds({
    subscriberIds,
    mutedSubscriberIds,
    historicalCommenterIds,
    participantId,
  });
  const isSubscribed = effectiveSubscribers.includes(userId);

  return {
    isSubscribed: !isSubscribed,
    subscriberIds: isSubscribed
      ? effectiveSubscribers.filter((id) => id !== userId)
      : uniqueUserIds([...effectiveSubscribers, userId]),
    mutedSubscriberIds: isSubscribed
      ? uniqueUserIds([...normalizeThreadUserIds(mutedSubscriberIds), userId])
      : normalizeThreadUserIds(mutedSubscriberIds).filter(
          (id) => id !== userId,
        ),
  };
};
